// Tonight — curated "tonight in Havana" section. Reuses Tonight businesses
// from the home composition. No new category-chip / filter UI here; if the user
// iter133 — Adds a header filter button that opens the shared
// FilterPlacesSheet. Filters apply client-side over the cached tonight list.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, SlidersHorizontal } from "lucide-react";
import { cachedBusinessGet } from "../lib/businessCache";
import { PlaceRow } from "../components/PlaceCard";
import { BottomNav } from "../components/BottomNav";
import ExchangeRateStrip from "../components/ExchangeRateStrip";
import WeatherWidget from "../components/WeatherWidget";
import { useI18n } from "../lib/i18n";
import FilterPlacesSheet, { DEFAULT_FILTERS, countActiveFilters, applyFilters } from "../components/FilterPlacesSheet";
import { useGps } from "../lib/useGps";

export default function Tonight() {
  const { t } = useI18n();
  const { coords } = useGps();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [filterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // iter119 — Tonight payload cached to `habano.cache.tonight` so the
    // page renders from local storage when the network is dead.
    cachedBusinessGet("/businesses?sort=tonight&limit=24", "tonight")
      .then(({ data }) => { if (!cancelled) setItems(Array.isArray(data) ? data : []); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const visible = useMemo(() => applyFilters(items, filters, { coords }), [items, filters, coords]);
  const activeCount = countActiveFilters(filters);

  return (
    <main data-testid="tonight-page" className="min-h-screen pb-32" style={{ background: "#0B0B0B" }}>
      <header className="px-5 pt-6 flex items-start gap-3">
        <Link
          to="/journey"
          data-testid="tonight-back"
          className="grid place-items-center rounded-full hover:bg-white/[0.04] transition-colors"
          style={{ minHeight: 48, minWidth: 48, color: "#F5F1E8" }}
          aria-label="Back"
        >
          <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
        </Link>
        <div className="flex-1 min-w-0 pt-1">
          <div style={{ color: "#E8B84B", fontFamily: "'DM Sans', sans-serif", fontSize: 11, letterSpacing: "0.18em", textTransform: "uppercase" }}>
            {t("tonight_eyebrow")}
          </div>
          <h1 className="mt-1" style={{ fontFamily: "'Playfair Display', serif", fontSize: 28, color: "#F5F1E8", lineHeight: 1.15 }}>
            {t("tonight_title")}
          </h1>
        </div>
        {/* iter133 — Filter button. Opens the shared FilterPlacesSheet.
            Shows numbered badge when filters are active. */}
        <button
          type="button"
          data-testid="tonight-filter-btn"
          aria-label="Open filters"
          onClick={() => setFilterOpen(true)}
          className="relative grid place-items-center rounded-full transition-colors hover:bg-[rgba(232,184,75,0.08)]"
          style={{
            width: 30, height: 30, minWidth: 30,
            border: "1px solid rgba(232,184,75,0.5)",
            color: "#E8B84B",
          }}
        >
          <SlidersHorizontal className="w-4 h-4" strokeWidth={1.5} />
          {activeCount > 0 && (
            <span
              data-testid="tonight-filter-badge"
              className="absolute"
              style={{
                top: -6, right: -6,
                minWidth: 18, height: 18, padding: "0 5px",
                background: "#E8B84B", color: "#1a0f06",
                borderRadius: 9999,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 11, fontWeight: 700,
                display: "grid", placeItems: "center",
              }}
            >
              {activeCount}
            </span>
          )}
        </button>
      </header>

      {/* Weather + Exchange rate row — same building blocks as Home */}
      <section className="px-5 mt-5 space-y-3" data-testid="tonight-context">
        <WeatherWidget />
        <ExchangeRateStrip />
      </section>

      {/* Tonight businesses list */}
      <section className="px-5 mt-6 space-y-3" data-testid="tonight-list">
        {loading ? (
          <div className="py-10 text-center text-foreground/45 text-sm">{t("loading") || "Loading…"}</div>
        ) : visible.length === 0 ? (
          <div className="py-10 text-center text-foreground/55 text-sm" data-testid="tonight-empty">
            {t("tonight_empty") || "No spots are open tonight."}
          </div>
        ) : (
          visible.map((b) => (
            <PlaceRow key={b.id} b={b} />
          ))
        )}
      </section>

      <FilterPlacesSheet
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={setFilters}
        currentFilters={filters}
        businesses={items}
        resultCount={visible.length}
      />

      <BottomNav />
    </main>
  );
}
